/**
 * BLOK-OMBOUW — STAP 2/3: schil om de bestaande engine.
 *
 * De engine wordt NIET opengebroken: per compleet blok roepen we de bestaande
 * genereerMaandrooster aan (startWeek = eerste blokweek, aantalWeken = bloklengte;
 * 4, of 5 voor blok 13 in een W53-jaar). De maand is alleen een kijkvenster:
 * uit de blokresultaten knippen we de maand-weken voor weergave; de balans
 * blijft PER BLOK (de rekeneenheid). Dezelfde week geeft daardoor altijd
 * hetzelfde rooster, ongeacht via welke maand ernaar gekeken wordt
 * (consistentie geverifieerd in het stap-2-harnas, %TEMP%\blok-stap2).
 *
 * Afwezigheid: per blokweek wordt de map gebouwd met het isoJaar van het blok
 * (zelfde aanpak als wekenInfo in de store), zodat jaargrens-maanden kloppen.
 * De engine nummert intern startWeek+i door; blokweken zijn aaneengesloten,
 * dus afwMaps op weeknummer keyen sluit daar exact op aan.
 */
import {
  genereerMaandrooster, isBeschikbaar, schoolweekType, berekenBezettingsDoel,
  telAaneengeslotenGewerkteDagen, telAaneengeslotenGewerkteDagenNa, MAX_AANEENGESLOTEN_WERKDAGEN,
} from './rooster-engine.js';
import { analyseerWeek } from './analyseer-week.js';
import { blokkenVoorMaand, heeftWeek53 } from '../data/blok.js';
import { bouwAfwezigheidMap, bouwAfwezigheidTypeMap } from '../data/afwezigheid.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';
import { bouwAfwezigheidUrenMap } from '../data/verlofuren.js';

// vastgelegde (optioneel): { [`${isoJaar}:${weeknummer}`]: weekObject } — globale set
// vastgelegde weken. Per blok wordt die gefilterd op het isoJaar van het blok en als
// { [weeknummer]: weekObject } aan genereerMaandrooster meegegeven.
export function genereerMaandViaBlokken(regels, afwezigheid, jaar, maand, vastgelegde = null, vakanties = []) {
  const { maandWeken, aantal, blokken } = blokkenVoorMaand(jaar, maand);

  const blokResultaten = blokken.map((bl) => {
    const startWeek = bl.weken[0];
    const aantalWeken = bl.weken.length; // 4, of 5 voor blok 13 in een W53-jaar

    // Alle weken van een blok horen bij hetzelfde isoJaar (anker = week 1 van bl.isoJaar)
    const afwMaps = {};
    const afwTypeMaps = {};
    const afwUrenMaps = {};
    const vakantieMaps = {};
    for (const wk of bl.weken) {
      afwMaps[wk] = bouwAfwezigheidMap(afwezigheid, wk, bl.isoJaar);
      afwTypeMaps[wk] = bouwAfwezigheidTypeMap(afwezigheid, wk, bl.isoJaar);
      // Handmatig ingevulde uren (verlof, ziekte), per dag al verdeeld over de HELE periode. Hier
      // gebouwd en niet in de engine: alleen op deze plek zijn de rauwe records én de
      // vakanties-lijst nog beschikbaar. Zonder ingevulde uren is de map leeg en verandert er
      // niets (data/verlofuren.js).
      afwUrenMaps[wk] = bouwAfwezigheidUrenMap(afwezigheid, wk, bl.isoJaar, regels, vakanties);
      // Per-week vakantiedagen (Set) — EXPLICIETE vakanties-lijst, nooit de helper-seed-default.
      vakantieMaps[wk] = new Set(vakantieDagenInWeek(wk, bl.isoJaar, vakanties));
    }

    // Vastgelegde weken voor DIT blok: filter op isoJaar+weeknummer.
    let vastBlok = null;
    if (vastgelegde) {
      for (const wk of bl.weken) {
        const v = vastgelegde[`${bl.isoJaar}:${wk}`];
        if (v) (vastBlok ||= {})[wk] = v;
      }
    }

    // ── Voorgeschiedenis over de blokgrens ────────────────────────────────────
    // De week direct vóór dit blok, UITSLUITEND als die is VASTGELEGD. Nodig om aaneengesloten
    // gewerkte dagen door te kunnen tellen over de grens heen; blokken worden immers los van
    // elkaar gegenereerd. Ligt de week niet vast, dan is er geen betrouwbare bron voor de dagen
    // ervóór (een verse generatie van het vorige blok kan elk moment anders uitvallen) en start
    // de teller blanco — zie de [REEKS]-logregel verderop.
    // JAARGRENS: bij blok 1 is startWeek - 1 gelijk aan 0. De voorgaande week is dan de LAATSTE
    // week van isoJaar - 1: 53 in een W53-jaar, anders 52.
    const vorigIsoJaar = startWeek === 1 ? bl.isoJaar - 1 : bl.isoJaar;
    const vorigWeeknr = startWeek === 1 ? (heeftWeek53(vorigIsoJaar) ? 53 : 52) : startWeek - 1;
    const voorgeschiedenis = vastgelegde?.[`${vorigIsoJaar}:${vorigWeeknr}`] || null;

    if (voorgeschiedenis) {
      // De teller leest ook de afwezigheid/vakantiedagen van die week, dus die maps krijgen er
      // een entry bij — gebouwd met vorigIsoJaar, wat op de jaargrens een ANDER jaar is dan het
      // blok. Botst nooit met een blokweek (het weeknummer ligt er per definitie vóór, en op de
      // jaargrens is het 52/53 tegenover 1-4), en de engine indexeert deze maps uitsluitend op
      // startWeek..startWeek+aantalWeken-1 — daar is een extra sleutel onzichtbaar.
      afwMaps[vorigWeeknr] = bouwAfwezigheidMap(afwezigheid, vorigWeeknr, vorigIsoJaar);
      vakantieMaps[vorigWeeknr] = new Set(vakantieDagenInWeek(vorigWeeknr, vorigIsoJaar, vakanties));
    }

    // verse kopie van de regels per blok-run: blokken mogen elkaar nooit beïnvloeden
    const res = genereerMaandrooster(
      JSON.parse(JSON.stringify(regels)), afwMaps, startWeek, aantalWeken, vastBlok, afwTypeMaps, vakantieMaps, voorgeschiedenis, afwUrenMaps
    );

    // Blok-brede nabewerking: herverdeel late-diensten cross-week binnen dit blok (block-neutraal).
    // Muteert res.weken in-place vóór perWeek wordt gebouwd. afwMaps is nodig voor isBeschikbaar.
    // VERGRENDELDE weken blijven erbuiten — niet als gat, niet als donor. res.weken draagt voor
    // die weken het OPGESLAGEN weekobject bij REFERENTIE (de vast-tak in draaiMaandrooster),
    // dus een verschuiving muteert de opslag zelf. Zelfde bescherming als de [HERPASS]-markering
    // in genereerMaandrooster. vastBlok is per blok het enige vergrendeld-signaal;
    // zonder vergrendelde weken (null) blijft het gedrag exact als voorheen.
    const vastNrs = vastBlok ? new Set(Object.keys(vastBlok).map(Number)) : null;
    // voorgeschiedenis: alleen om de reeks-rem over de blokgrens te laten kijken. herverdeelLate
    // zet die week in een LOKALE array voor de teller; res.weken blijft ongemoeid, want de week
    // ligt vast en komt bij referentie uit de opslag.
    herverdeelLate(res.weken, regels, afwMaps, vakantieMaps, vastNrs, voorgeschiedenis);

    // De BLINDE START zichtbaar maken. Zonder vastgelegde week vóór de blokgrens heeft de
    // dagenreeks-teller geen voorgeschiedenis en begint hij bij 0: een reeks die over de grens
    // doorloopt wordt dan te kort geteld. Dat gat hoort in het log te staan i.p.v. stil te zijn.
    // De regel gaat naar de eerste NIET-vastgelegde week van het blok; een vastgelegde week komt
    // bij referentie uit de opslag (zie de comment hierboven) en mag niet gemuteerd worden.
    if (!voorgeschiedenis) {
      const doelWeek = res.weken.find(w => !vastNrs?.has(w.weeknummer));
      doelWeek?.log?.unshift(
        `[REEKS] blok ${bl.blok}: blinde start bij wk${startWeek} — week ${vorigIsoJaar}:${vorigWeeknr} ligt niet vast, `
        + 'dus de teller voor aaneengesloten gewerkte dagen begint op 0 en telt niet door over de blokgrens'
      );
    }

    return {
      blok: bl.blok,
      isoJaar: bl.isoJaar,
      weken: bl.weken,
      inMaand: bl.inMaand,
      extraWeken: bl.extraWeken,
      bevatWeek53: bl.bevatWeek53,
      // samenvatting per blok = de rekeneenheid: target = bloklengte x contract
      samenvatting: res.samenvatting,
      // volledige week-resultaten van het blok, op weeknummer ontsloten
      perWeek: Object.fromEntries(res.weken.map(w => [w.weeknummer, w])),
    };
  });

  // Maand-weergave: de maand-weken uit de blokresultaten knippen.
  // Elke maand-week komt uit precies één blok (blokken overlappen niet).
  // Het volledige engine-weekobject (rooster, conflicten, log, schoolweekType,
  // urenTeller, ...) gaat mee zodat de bestaande UI er niets aan mist.
  const weergave = maandWeken.map(({ week, isoJaar, maandag }) => {
    const bron = blokResultaten.find(b => b.isoJaar === isoJaar && b.weken.includes(week));
    return { ...bron.perWeek[week], blok: bron.blok, isoJaar, maandag };
  });

  return { jaar, maand, aantal, maandWeken, blokken: blokResultaten, weergave };
}

/**
 * Blok-brede late-herverdeel-pass.
 *
 * Per gemeente, voor de WEEKDAG-late-diensten: vul een onderbezet late-slot (onder het
 * dienst-minimum) door personen BLOCK-NEUTRAAL te verschuiven vanaf surplus-slots (boven
 * minimum) elders in HETZELFDE blok. Block-neutraal = zelfde 8u, blijft binnen het blok →
 * de blok-balans (contract-uren over de bloklengte) blijft kloppen.
 *
 * Als donorbron tellen zowel late-diensten als DAGDIENSTEN; het GAT blijft altijd een
 * late-slot. Zie de comment bij de scan hieronder waarom die twee kanten verschillen.
 *
 * Werkt op res.weken: weken[i].rooster[gem][dienstId][dag] (namen-array), weken[i].urenTeller,
 * weken[i].weekCaps. Muteert in-place en geeft `weken` terug. `afwMaps` ({ [weeknummer]:
 * { naam: Set<dag> } }) is nodig voor isBeschikbaar (afwezigheid); de blok-generator bouwt die al.
 *
 * `vergrendeld` (Set<weeknummer> | null): die weken blijven volledig buiten de herverdeling —
 * niet als gat en niet als donor. Ze komen ongewijzigd uit de engine terug (bij referentie op
 * het opgeslagen weekobject), dus muteren zou de vastgelegde week corrumperen. null = niets
 * vergrendeld = ongewijzigd gedrag.
 *
 * `voorgeschiedenis` (weekobject | null): de VASTGELEGDE week direct vóór dit blok. Uitsluitend
 * om de reeks-rem hieronder over de blokgrens te laten terugtellen; die week wordt nooit gemuteerd
 * en komt niet in `weken` terecht. null = terugtellen begint blanco bij de eerste blokweek, wat
 * de blok-generator al als [REEKS]-blindestart logt.
 */
function herverdeelLate(weken, regels, afwMaps = {}, vakantieMaps = {}, vergrendeld = null, voorgeschiedenis = null) {
  const WEEKDAGEN = ['ma', 'di', 'wo', 'do', 'vr'];
  const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']; // rust-horizon (dag erná)
  const kg = regels.instellingen?.koppel_grootte || 2;

  // naam -> medewerker-key (voor config-lookup + isBeschikbaar)
  const keyVanNaam = {};
  for (const [k, m] of Object.entries(regels.medewerkers)) {
    if (k.startsWith('_') || typeof m !== 'object' || m === null) continue;
    keyVanNaam[m.naam] = k;
  }

  const dienstVan = (gem, dienstId) => regels.gemeenten[gem]?.diensten?.[dienstId];
  const dagLijstVan = (d, type) =>
    (type === 'A' && d.week_a_dagen) ? d.week_a_dagen
      : (type === 'B' && d.week_b_dagen) ? d.week_b_dagen
        : (d.week_a_dagen || d.week_b_dagen) ? [] : (d.dagen || []);

  // ── 1+2+3: scan alle WEEKDAG-slots → gaten (< min) en donoren (> min) ───────────
  //
  // De twee kanten hebben een EIGEN filter. Die asymmetrie is opzet, geen slordigheid:
  //
  //   GATEN — alleen late-diensten. Een leeg dagdienst-slot betekent niet dat er iets mist:
  //   in een schoolvakantie draait de engine uitsluitend late diensten en zet hij de
  //   dagdienst-dag bewust op [] (rooster-engine.js:516-517). Op de live-data van 9 aug 2026
  //   zijn dat 42 slots. Zou de gaten-scan die meenemen, dan las hij ze als onderbezetting en
  //   ging deze pass ze "repareren" terwijl er niets aan de hand is.
  //
  //   DONOREN — late-diensten én dagdiensten. Een dagdienst boven zijn minimum is net zo goed
  //   8 uur die block-neutraal te verplaatsen valt, en zonder die bron blijft een late-gat open
  //   terwijl iemand diezelfde week overtollig op een dagdienst staat (wk44 2026: Leroy). Een
  //   leeg vakantie-dagdienstslot komt hier vanzelf niet in — leeg is nooit bóven het minimum.
  //
  // Wat NIET verandert: het gat is en blijft een late-slot, dus alle eligibility hieronder
  // (alleen_dagdienst, geen_late_dienst_dagen, de 11-uursrust) toetst nog steeds op een late.
  const isGatDienst = (id) => id.startsWith('late_dienst');
  const isDonorDienst = (id) => id.startsWith('late_dienst') || id === 'dagdienst';

  const gaten = [];
  const donoren = [];
  weken.forEach((week, weekIdx) => {
    if (vergrendeld?.has(week.weeknummer)) return;              // vergrendeld: geen gat, geen donor
    for (const gem of Object.keys(week.rooster)) {
      for (const dienstId of Object.keys(week.rooster[gem])) {
        const gatKant = isGatDienst(dienstId);
        const donorKant = isDonorDienst(dienstId);
        if (!gatKant && !donorKant) continue;
        const dienst = dienstVan(gem, dienstId);
        if (!dienst) continue;
        const min = berekenBezettingsDoel(dienst, false, kg);
        for (const dag of Object.keys(week.rooster[gem][dienstId])) {
          if (!WEEKDAGEN.includes(dag)) continue;                   // pure weekend-slots (za/zo) overslaan
          const namen = week.rooster[gem][dienstId][dag];
          if (!Array.isArray(namen)) continue;
          if (gatKant && namen.length < min) gaten.push({ weekIdx, weeknummer: week.weeknummer, gem, dienstId, dag, min });
          else if (donorKant && namen.length > min) donoren.push({ weekIdx, weeknummer: week.weeknummer, gem, dienstId, dag, min });
        }
      }
    }
  });
  if (gaten.length === 0 || donoren.length === 0) return weken;     // niets te herverdelen

  // ── reeks-rem: een verschuiving mag de max-aaneengesloten-regel niet breken ─────
  // Zonder deze rem zet herverdeelLate iemand op een NIEUWE weekdag zonder enige reeks-
  // controle, en tilt zo een rooster dat de regel respecteert er alsnog overheen.
  // Zelfde grens en zelfde terugval als de engine (rooster-engine.js).
  const maxAaneengesloten = regels.instellingen?.max_aaneengesloten_werkdagen ?? MAX_AANEENGESLOTEN_WERKDAGEN;
  // LOKALE array voor de teller: de vastgelegde week vóór de blokgrens ervoor geplakt, zodat een
  // reeks die over de grens doorloopt niet te kort geteld wordt. `weken` zelf blijft ongemoeid —
  // die array wordt gemuteerd en de voorgeschiedenis-week ligt vast. De weekOBJECTEN zijn dezelfde
  // referenties, en dat is precies de bedoeling: de proefverschuiving hieronder is er zichtbaar in.
  const tellerWeken = voorgeschiedenis ? [voorgeschiedenis, ...weken] : [...weken];
  const reeksCtx = { afwMaps, vakantieMaps };

  /**
   * Hoe lang wordt de reeks rond de GAT-dag als deze verschuiving doorgaat?
   *
   * Meet op de ECHTE stand door de verschuiving even uit te voeren: de donordag valt weg, de
   * gat-dag komt erbij. Daardoor splitst een donordag die BINNEN de gemeten reeks ligt precies
   * zoals hij in werkelijkheid zou splitsen — zowel binnen dezelfde week als over een weekgrens
   * (gat op maandag, donor op de vrijdag ervóór) — zonder afstandsrekenwerk of speciale gevallen.
   * De reeks rónd de donordag hoeft niet apart gecontroleerd: een dag weghalen maakt een reeks
   * alleen korter, nooit langer.
   *
   * Vooruit én achteruit. telAaneengeslotenGewerkteDagen kijkt alleen terug, en dat is hier te
   * ruim: herverdeelLate plaatst in een rooster dat al af is, dus de dagen ná de gat-dag staan
   * al vast en kunnen de reeks overbruggen. Bij grens 5 met zo+ma gewerkt, dinsdag als gat en
   * wo/do/vr gewerkt geeft terugtellen 2 (+1 = 3, toegestaan), terwijl de echte reeks
   * zo-ma-di-wo-do-vr = 6 is.
   *
   * De proef wordt in een finally teruggedraaid, met de naam op EXACT dezelfde index: de volgorde
   * van dSlot voedt zowel de kandidaatlus als de tiebreak, en moet byte-identiek terugkomen.
   */
  const reeksNaVerschuiving = (mw, naam, dSlot, gatSlot, gat) => {
    const idx = dSlot.indexOf(naam);
    if (idx === -1) return Infinity;                    // staat er niet: nooit verschuiven
    dSlot.splice(idx, 1);                               // donordag valt weg
    gatSlot.push(naam);                                 // gat-dag komt erbij
    try {
      const voor = telAaneengeslotenGewerkteDagen(tellerWeken, mw, gat.weeknummer, gat.dag, reeksCtx);
      const na = telAaneengeslotenGewerkteDagenNa(tellerWeken, mw, gat.weeknummer, gat.dag, reeksCtx);
      return voor + 1 + na;
    } finally {
      gatSlot.pop();
      dSlot.splice(idx, 0, naam);                       // exact terug op zijn plek
    }
  };

  // ── eligibility/rust-helpers (1-op-1 met engine/diagnose) ───────────────────────
  const lingDienstOpDag = (mw, gem, dag, type) => {                 // vast_rotatiepatroon: voorkeur-gemeente draait die dag
    if (!(mw.vast_rotatiepatroon && mw.voorkeur_gemeente && mw.voorkeur_gemeente !== gem)) return false;
    const vg = regels.gemeenten[mw.voorkeur_gemeente];
    if (!vg || vg.actief === false) return false;
    return Object.values(vg.diensten).some(d => dagLijstVan(d, type).includes(dag));
  };
  const eligibleOpGat = (k, mw, gem, dag, weeknummer, type, afwMap) => {
    if (!mw.gemeenten?.includes(gem)) return false;
    if (!isBeschikbaar(k, mw, dag, gem, afwMap, type, weeknummer, vakantieMaps[weeknummer] || null)) return false;  // afwezig/vrij/school/kantoor/werkdagen/weekend-excl/rotatie
    if (mw.alleen_dagdienst && dag !== 'za' && dag !== 'zo') return false;        // alleen-dag mag geen weekdag-late
    if (mw.geen_late_dienst_dagen?.includes(dag)) return false;                   // per-dag late-uitsluiting
    if (lingDienstOpDag(mw, gem, dag, type)) return false;                        // Lingewaard-cyclus
    return true;
  };
  const alOpDag = (naam, weekObj, dag) =>
    Object.values(weekObj.rooster).some(g => Object.values(g).some(dm => Array.isArray(dm[dag]) && dm[dag].includes(naam)));
  const heeftDagdienstOp = (naam, dag, weekObj) =>
    Object.values(weekObj.rooster).some(g => Array.isArray(g.dagdienst?.[dag]) && g.dagdienst[dag].includes(naam));
  const rustOkVoorLate = (naam, dag, weekObj) => {                  // late geblokkeerd als dagdienst de ochtend erná (11u)
    const ni = DAGEN.indexOf(dag) + 1;
    return !(ni < DAGEN.length && heeftDagdienstOp(naam, DAGEN[ni], weekObj));
  };

  // ── 7: deterministische gat-volgorde: week asc, gemeente alfabetisch, dienst alfabetisch, dag in weekdag-volgorde
  gaten.sort((a, b) =>
    a.weeknummer - b.weeknummer
    || a.gem.localeCompare(b.gem)
    || a.dienstId.localeCompare(b.dienstId)
    || WEEKDAGEN.indexOf(a.dag) - WEEKDAGEN.indexOf(b.dag));

  // ── 4+5+6: vul elk gat tot aan zijn minimum via geldige verschuivingen ──────────
  const geraakteWeken = new Set(); // weekIdx (donor + gat) waar daadwerkelijk verschoven is — voor de conflicten-recompute
  for (const gat of gaten) {
    const gatWeekObj = weken[gat.weekIdx];
    const type = schoolweekType(gat.weeknummer);
    const afwMap = afwMaps[gat.weeknummer] || {};
    const gatSlot = gatWeekObj.rooster[gat.gem][gat.dienstId][gat.dag];

    while (gatSlot.length < gat.min) {
      // verzamel geldige (donor, persoon)-kandidaten — slots muteren, dus telkens her-evalueren
      const kandidaten = [];
      for (const donor of donoren) {
        if (donor.gem !== gat.gem) continue;                                                          // per gemeente
        if (donor.weekIdx === gat.weekIdx && donor.dienstId === gat.dienstId && donor.dag === gat.dag) continue; // niet zichzelf
        const dSlot = weken[donor.weekIdx].rooster[donor.gem][donor.dienstId][donor.dag];
        const overschot = dSlot.length - donor.min;
        if (overschot <= 0) continue;                                                                 // (b) geen surplus meer
        for (const naam of dSlot) {
          const k = keyVanNaam[naam];
          if (!k) continue;
          const mw = regels.medewerkers[k];
          if (!eligibleOpGat(k, mw, gat.gem, gat.dag, gat.weeknummer, type, afwMap)) continue;        // (a) eligibility
          if (alOpDag(naam, gatWeekObj, gat.dag)) continue;                                           // (a) niet al op gat-dag
          if (!rustOkVoorLate(naam, gat.dag, gatWeekObj)) continue;                                   // (c) rust 11u
          const reeksNa = reeksNaVerschuiving(mw, naam, dSlot, gatSlot, gat);                          // (e) max aaneengesloten
          if (reeksNa > maxAaneengesloten) {
            gatWeekObj.log?.push(
              `[REEKS] ${naam} niet verschoven naar ${gat.gem}.${gat.dienstId} ${gat.dag}: `
              + `zou een reeks van ${reeksNa} aaneengesloten dagen geven (max ${maxAaneengesloten})`
            );
            continue;
          }
          let dOk;                                                                                    // (d) block-neutraal; week-cap +1 dienst slack
          if (donor.weekIdx === gat.weekIdx) dOk = true;                                              // zelfde week → cap-neutraal
          else {
            const naUren = (gatWeekObj.urenTeller[k] || 0) + 8;          // urenTeller/weekCaps keyen op mwKey, niet naam
            const cap = gatWeekObj.weekCaps?.[k] ?? mw.contract_uren;
            dOk = naUren <= cap + 8;
          }
          if (!dOk) continue;
          kandidaten.push({ donor, dSlot, naam, k, overschot, weekAfstand: Math.abs(donor.weeknummer - gat.weeknummer) });
        }
      }
      if (kandidaten.length === 0) break;                                                             // geen geldige donor meer

      // KNOP 1: grootste overschot eerst, dan kleinste week-afstand; daarna deterministische tiebreaks
      kandidaten.sort((a, b) =>
        b.overschot - a.overschot
        || a.weekAfstand - b.weekAfstand
        || a.donor.weeknummer - b.donor.weeknummer
        || WEEKDAGEN.indexOf(a.donor.dag) - WEEKDAGEN.indexOf(b.donor.dag)
        || a.naam.localeCompare(b.naam));
      const beste = kandidaten[0];

      // ── voer de verschuiving uit (in-place) + urenTeller bijwerken (bron −8, doel +8) ──────────
      const idx = beste.dSlot.indexOf(beste.naam);
      if (idx === -1) break; // veiligheid
      beste.dSlot.splice(idx, 1);          // rooster blijft op NAAM
      gatSlot.push(beste.naam);            // rooster blijft op NAAM
      const dWeekObj = weken[beste.donor.weekIdx];
      dWeekObj.urenTeller[beste.k] = (dWeekObj.urenTeller[beste.k] || 0) - 8;     // urenTeller op mwKey
      gatWeekObj.urenTeller[beste.k] = (gatWeekObj.urenTeller[beste.k] || 0) + 8; // urenTeller op mwKey
      geraakteWeken.add(beste.donor.weekIdx);                                     // donor- en gat-week voor de recompute
      geraakteWeken.add(gat.weekIdx);
    }
  }

  // ── 8: onderbezetting-waarschuwing bijwerken voor de geraakte weken ─────────────
  // herverdeelLate muteerde het rooster, maar week.conflicten (de "slechts X/Y koppel"-melding,
  // in Stap 6 berekend MET het gat) is daardoor stale. We herberekenen die via analyseerWeek.
  // BELANGRIJK: UITSLUITEND week.conflicten updaten — NIET week.caoOvertredingen. De cross-week-
  // verschuiving duwt de gat-week bewust +8u boven de week-cap (gewenste schuifruimte); een recompute
  // van caoOvertredingen met de oorspronkelijke weekCaps zou dat als VALSE overtreding flaggen. De
  // bestaande week.caoOvertredingen (door de engine binnen de caps berekend) laten we ongemoeid.
  for (const weekIdx of geraakteWeken) {
    const week = weken[weekIdx];
    const analyse = analyseerWeek(
      week.rooster, regels, week.weeknummer, week.isoJaar, schoolweekType(week.weeknummer),
      { afwezigheid: afwMaps[week.weeknummer] || {}, weekCaps: week.weekCaps, vakantieDagen: vakantieMaps[week.weeknummer] || null },
    );
    week.conflicten = analyse.conflicten;
  }

  return weken;
}
